import { AgxErrorLabel } from '@urbanx/agx.ui-components2';
import { useMemo, useState } from 'react';
import { AgxSelect, AgxMultiSelect, AgxColumn, AgxLabel, AgxRow } from '@urbanx/agx-ui-components';
import { isEqual } from 'lodash-es';
import { IPermission } from 'types/agency';
import { AgencyAgentModel } from '@urbanx/agx.agencies.client';

interface IAgentsSelectorProps {
  agents: AgencyAgentModel[];
  permission: IPermission;
  setPermission: (
    updatePermission: (previousState: IPermission | undefined) => IPermission
  ) => void;
}

const createOptions = (
  selectedAgentId: string,
  agentOptions: { value: string; label: string }[]
) => {
  return [
    {
      value: '',
      label: 'Please select an agent',
    },
  ].concat(agentOptions.filter((agent) => agent.value !== selectedAgentId));
};

const AgentsSelector = ({ agents, permission, setPermission }: IAgentsSelectorProps) => {
  const [hasLeadAgentChanged, setHasLeadAgentChanged] = useState(false);

  const agentOptions = useMemo(
    () =>
      agents.map((agent) => {
        return {
          value: agent.id!,
          label: `${agent.name?.firstName} ${agent.name?.lastName}`,
        };
      }),
    [agents]
  );

  const leadAgentOptions = useMemo(
    () => createOptions(permission.secondAgentId!, agentOptions),
    [agentOptions, permission.secondAgentId]
  );

  const secondAgentOptions = useMemo(
    () => createOptions(permission.leadAgentId!, agentOptions),
    [agentOptions, permission.leadAgentId]
  );

  const leadAgentDefaultValue = useMemo(() => {
    return leadAgentOptions.find((agent) => agent.value === permission.leadAgentId);
  }, [leadAgentOptions, permission.leadAgentId]);

  const secondAgentDefaultValue = useMemo(() => {
    return secondAgentOptions.find((agent) => agent.value === permission.secondAgentId);
  }, [permission.secondAgentId, secondAgentOptions]);

  const createdByUser = useMemo(
    () => agents.find((agent) => agent.id === permission.createdByUserId),
    [agents, permission.createdByUserId]
  );

  if (!permission) return null;

  const leadAgentError =
    hasLeadAgentChanged && !permission.leadAgentId?.trim()
      ? 'Select a lead agent before saving campaign ownership.'
      : undefined;

  return (
    <AgxColumn extraLargeGap extraClasses={'panelTitleContainer'}>
      <AgxRow>
        <AgxLabel medium>Created By:&nbsp;&nbsp;</AgxLabel>
        <AgxLabel medium>{`${createdByUser?.name?.firstName ?? ''} ${
          createdByUser?.name?.lastName ?? ''
        }`}</AgxLabel>
      </AgxRow>
      <AgxColumn smallGap>
        <AgxSelect
          id={`leadAgent`}
          label="Lead Agent"
          error={leadAgentError}
          hideErrorDescription
          options={leadAgentOptions}
          onValueChanged={({ value }) => {
            if (value !== (leadAgentDefaultValue?.value ?? '')) setHasLeadAgentChanged(true);
            setPermission((previousState) => ({
              ...previousState,
              leadAgentId: value,
            }));
          }}
          required
          defaultValue={leadAgentDefaultValue}
        />
        {leadAgentError && (
          <div role="alert">
            <AgxErrorLabel id="leadAgent" errorText={leadAgentError} />
          </div>
        )}
      </AgxColumn>
      <AgxSelect
        id={`secondAgent`}
        label="Second Agent"
        options={secondAgentOptions}
        onValueChanged={({ value }) =>
          setPermission((previousState) => ({
            ...previousState,
            secondAgentId: value,
          }))
        }
        defaultValue={secondAgentDefaultValue}
      />

      <AgxMultiSelect
        id={`userAccess`}
        label="Additional User Access"
        hideOptionalLabel
        defaultValue={permission.assignedAgentIds ?? []}
        onValueChanged={({ value }: { value: string[] }) => {
          if (!isEqual(value, permission.assignedAgentIds)) {
            setPermission((previousState) => ({
              ...previousState,
              assignedAgentIds: value,
            }));
          }
        }}
        options={agentOptions}
      />
    </AgxColumn>
  );
};

export default AgentsSelector;
